import { Drawer, Collapse } from 'antd';
import { ExclamationCircleFilled, CloseOutlined, CheckCircleFilled } from '@ant-design/icons';
import { colorConstants } from 'config/colorConstants';
import { InfeasibilityConstraint, InfeasibilityConstraintGroup } from 'types/tradePromoOptimization';

const { Panel } = Collapse;

interface InfeasiblePlanDrawerProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    relaxedConstraints: InfeasibilityConstraintGroup[];
    width?: number;
    topOffset?: number;
}

const ConstraintItemRow = ({ constraint }: { constraint: InfeasibilityConstraint }) => {
    const hasInfeasibility = constraint.infeasible;

    return (
        <div className="py-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {hasInfeasibility ? (
                        <ExclamationCircleFilled style={{ color: colorConstants.red, fontSize: 14 }} />
                    ) : (
                        <CheckCircleFilled style={{ color: colorConstants.limeGreen, fontSize: 14 }} />
                    )}
                    <span className="font-medium text-sm">{constraint.constraint_name}</span>
                </div>
                <span className={`text-sm ${hasInfeasibility ? 'text-red-500 font-medium' : 'text-gray-600'}`}>
                    {hasInfeasibility ? 'Infeasible' : 'Feasible'}
                </span>
            </div>
        </div>
    );
};

const PanelHeader = ({ title, hasInfeasibility }: { title: string; hasInfeasibility: boolean }) => (
    <div className="flex items-center gap-2">
        {hasInfeasibility ? (
            <ExclamationCircleFilled style={{ color: colorConstants.red, fontSize: 14 }} />
        ) : (
            <CheckCircleFilled style={{ color: colorConstants.limeGreen, fontSize: 14 }} />
        )}
        <span className="font-semibold">{title}</span>
    </div>
);

const InfeasiblePlanDrawer = ({
    open,
    onClose,
    title = 'Infeasible Plan Reasons',
    relaxedConstraints,
    width = 450,
    topOffset = 0,
}: InfeasiblePlanDrawerProps) => {
    const defaultActiveKeys =
        relaxedConstraints?.filter((group) => group.infeasible)?.map((group) => group.constraint_group) || [];

    const activeKeys =
        defaultActiveKeys.length > 0
            ? defaultActiveKeys
            : relaxedConstraints.length > 0
            ? [relaxedConstraints[0].constraint_group]
            : [];

    return (
        <Drawer
            title={
                <div className="flex justify-between items-center w-full">
                    <span className="font-semibold text-base">{title}</span>
                    <CloseOutlined onClick={onClose} className="cursor-pointer text-gray-500 hover:text-gray-700" />
                </div>
            }
            placement="right"
            onClose={onClose}
            open={open}
            width={width}
            closable={false}
            bodyStyle={{ padding: '16px' }}
            headerStyle={{ borderBottom: 'none', padding: '16px 24px' }}
            contentWrapperStyle={{ top: topOffset, height: `calc(100% - ${topOffset}px)` }}
            maskStyle={{ top: topOffset }}
        >
            <Collapse
                defaultActiveKey={activeKeys}
                expandIconPosition="end"
                bordered={false}
                style={{ background: 'transparent' }}
            >
                {relaxedConstraints.map((group) => (
                    <Panel
                        key={group.constraint_group}
                        header={<PanelHeader title={group.constraint_group} hasInfeasibility={group.infeasible} />}
                        style={{
                            marginBottom: 12,
                            background: '#f5f5f5',
                            borderRadius: 8,
                            border: 'none',
                        }}
                    >
                        <div className="rounded-md p-3">
                            {group.constraints.map((constraint, index) => (
                                <ConstraintItemRow key={index} constraint={constraint} />
                            ))}
                        </div>
                    </Panel>
                ))}
            </Collapse>
        </Drawer>
    );
};

export default InfeasiblePlanDrawer;
